import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import Rodal from '../../../components/Rodal/Rodal';
import { Row, ModalBody, ModalHeader } from 'reactstrap';
import './NdaTermsPopup.scss';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { useStateSelector } from '../../../store/selectors';
import createNotification from '../../../utils/createNotification';
import { NdaTerms } from '../../../slices/marketplace/timeline/models/nda-term-model';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { DataNdaTermsType } from '../../../slices/marketplace/timeline/models/enums/data-nda-term-type';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { EngagementData } from '../../../slices/marketplace/timeline/models/engagement-data.model';
import { NdaTermsForm } from './NdaTermsForm/NdaTermsForm';
import { EngagementProcess } from '../../../slices/marketplace/timeline/models/engagement-process.model';
import BuyerProfilePreviewPopup from '../../../components/BuyerProfilePreviewPopup/BuyerProfilePreviewPopup';
import { 
    NdaTermStatus,
    NdaTermStatusList,
} from '../../../slices/marketplace/timeline/models/enums/nda-term-status';
import { NdaTermStep } from '../../../slices/marketplace/timeline/models/enums/nda-term-step';
import { LockInPopup } from './LockInPopup';
import { USER_ROLES } from '../../../utils/constants';
import { EngagementBuyer } from '../../../slices/marketplace/timeline/models/engagement-buyer.model';
import { MarketplaceBuyer } from '../../../slices/marketplace/buyer/models/buyer';
import PortalTooltip from '../../../components/PortalTooltip/PortalTooltip';
import { ExtendDeadlinePopup } from './ExtendDeadlinePopup/ExtendDeadlinePopup';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import {
    MarketplaceOpportunityStatus,
    MarketplaceOpportunityStatusList,
} from '../../../slices/marketplace/opportunity/models/opportunity-status';
import { TimelineActionStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { EngagementProcessStatus } from '../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import InfoTooltip from '../../../components/info-tooltip/info-tooltip';
import BuyerSelectionPaymentComponent, {
    SelectionPaymentDetails,
} from '../../../components/PaymentDialog/BuyerSelectionPaymentComponent/BuyerSelectionPaymentComponent';
import { BuyerSelectionType } from '../../../wallet/models/BuyerSelectionType';
import { useDispatch } from 'react-redux';
import { LoadMarketplacePricing } from '../../../slices/marketplace/pricing/load-marketplace-pricing.thunk';
import { LevelOfService, isSfas } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { useBuyerSelectionPaidBefore } from '../../../custom-hooks/useBuyerSelectionPaidBefore';
import { PricesCalculationHelper } from '../../../utils/pricesCalculationHelper';
import { generateGuid } from '../../../utils/typedHelper';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { getEventDeadlineDateUtc } from '../../../slices/marketplace/timeline/utils/timeline-dates';

interface BuyerInclusionStatus {
    buyerId: number;
    active: boolean;
}

interface DataNdaTermsPopupProps {
    isVisible: boolean;
    setIsVisible(value: boolean): void;
    fetchData(): void;
    activeEventId: string;
    events: TimelineEvent[];
    engagementData: EngagementData;
    engagementProcess: EngagementProcess;
    engagementId: number;
    levelOfService: LevelOfService;
    // [#1299, E2E run 3 G7] On the catch-up: the Seller's own process, every buyer of the engagement. The Data NDA's
    // "first X as one" counts the buyers already sent it there too, as the API prices it (345548c).
    sellersOwnProcess?: EngagementProcess;
}

const NdaTermsPopup = (props: DataNdaTermsPopupProps) => {
    const isMarketplaceBuyer = useStateSelector(
        (s) => s.userSummary?.summary?.isMarketplaceBuyer
    );
    const party = useCurrentMarketplaceParty(props.engagementId);
    const isBroker = party === TimelineParty.Broker;
    const isObserver = party === TimelineParty.Observer;
    const activeEventActionId = props.events.find(
        (e) => e.id === props.activeEventId
    )?.linkedAction?.id;
    const [currentFormState, setCurrentFormState] = useState<NdaTerms>({
        id: null,
        nonDisclosurePeriod: null,
        nonSolicitationDvmNamesVisible: null,
        nonSolicitationDvmNamesBlinded: null,
        nonSolicitationStaffNamesVisible: null,
        nonSolicitationStaffNamesBlinded: null,
        buyerId: null,
        party: isMarketplaceBuyer ? TimelineParty.Buyer : TimelineParty.Seller,
        dateCreated: new Date(),
        dateModified: new Date(),
        isPreferences: false,
        agreedTermsToDocumentList: [],
    });
    const [lastFormState, setLastFormState] = useState<NdaTerms>(null);
    const [buyersInclusionStatuses, setBuyersInclusionStatuses] = useState<
        BuyerInclusionStatus[]
    >([]);
    const [isBuyerProfilePopupVisible, setIsBuyerProfilePopupVisible] =
        useState(false);
    const [isLockInPopupVisible, setIsLockInPopupVisible] = useState(false);
    const [isExtendDeadlinePopupVisible, setIsExtendDeadlinePopupVisible] =
        useState(false);

    const [selectedBuyerId, setSelectedBuyerId] = useState(0);
    const [isSellerReadOnly, setIsSellerReadOnly] = useState(false);
    const [isBuyerReadOnly, setIsBuyerReadOnly] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isAdminView, setIsAdminView] = useState(true);
    const [isConfirmationPopupVisible, setIsConfirmationPopupVisible] =
        useState(false);
    const [isSwitchingBuyer, setIsSwitchingBuyer] = useState(false);
    const buyer = useStateSelector((state) => state.buyersState.targetBuyer);
    const axios = useStateSelector((state) => state.core.axios);
    const [isFinalLockIn, setIsFinalLockIn] = useState(false);
    const targetEvent = props.events.find(
        (event) => event.id === props.activeEventId
    );
    const isEventOrEngagementCompleted =
        targetEvent.status === TimelineEventStatus.Completed ||
        props.engagementProcess?.status === EngagementProcessStatus.Completed;
    const [isPaymentPopupVisible, setIsPaymentPopupVisible] = useState(false);
    const [selectionPaymentDetails, setSelectionPaymentDetails] =
        useState<SelectionPaymentDetails>(null);
    const marketplacePricing = useStateSelector(
        (state) => state.marketplacePricing.pricing
    );
    const dispatch = useDispatch() as any;
    const [alreadySelectedBuyerIds, setAlreadySelectedBuyerIds] = useState<number[]>([]);
    const paidBeforeBuyerIds = useBuyerSelectionPaidBefore(
        props.engagementId,
        props.activeEventId,
        !isMarketplaceBuyer && !isSfas(props.levelOfService)
    );

    useEffect(() => {
        if (isMarketplaceBuyer) {
            return;
        }
        dispatch(LoadMarketplacePricing(props.engagementId));
        setAlreadySelectedBuyerIds(
            getBuyersWithReceivedDocuments(props.sellersOwnProcess ?? props.engagementProcess)
        );
    }, []);

    useEffect(() => {
        const summary = JSON.parse(localStorage.getItem('loggedInUserSummary'));
        const isAdmin = summary?.roleId === USER_ROLES.ADMIN;
        setIsAdminView(isAdmin);

        if (isAdmin || isBroker || isObserver) {
            setIsBuyerReadOnly(true);
            setIsSellerReadOnly(true);
        }

        if (isEventOrEngagementCompleted) {
            setIsBuyerReadOnly(true);
            setIsSellerReadOnly(true);
        }

        if (
            !props.engagementProcess?.buyers ||
            props.engagementProcess?.buyers?.length == 0
        )
            setBuyersInclusionStatuses([]);
        else
            setBuyersInclusionStatuses([
                ...props.engagementProcess.buyers?.map((b) => {
                    const actionStatus = b.actionState.find(
                        (s) => s.actionId == activeEventActionId
                    )?.buyerStatus;
                    return {
                        buyerId: b.buyerId,
                        active:
                            actionStatus ===
                                MarketplaceOpportunityStatus.AwaitingBuyerResponse ||
                            actionStatus ===
                                MarketplaceOpportunityStatus.AwaitingSellerResponse ||
                            actionStatus ===
                                MarketplaceOpportunityStatus.Engaged,
                    };
                }),
            ]);

        const selection = getNdaTermSelection();
        if (!selection) return;

        const buyerNdaTerms = selection.ndaTermsList.filter(
            (x) => x.party === TimelineParty.Buyer && x.buyerId === buyer?.id
        );

        const sellerNdaTerms = selection.ndaTermsList.filter(
            (x) => x.party === TimelineParty.Seller
        );

        if (!isMarketplaceBuyer) {
            if (selection.currentStep === NdaTermStep.TermsAgreed) {
                setIsBuyerReadOnly(true);
                setIsSellerReadOnly(true);
            }
            if (sellerNdaTerms.length > 0) {
                const lastSellerNda = getLastTermSelection(sellerNdaTerms);
                setCurrentFormState({ ...lastSellerNda, id: null });
                setLastFormState({ ...lastSellerNda });
            }
        } else {
            if (buyerNdaTerms.length > 0) {
                const lastBuyerNda = getLastTermSelection(buyerNdaTerms);
                setCurrentFormState(lastBuyerNda);
                if (lastBuyerNda.agreedTermsToDocumentList?.length) {
                    setIsBuyerReadOnly(true);
                    setIsSellerReadOnly(true);
                }
            } else {
                const ndaTermPreferences =
                    getNdaTermPreferencesForMarketplaceBuyer(buyer);
                setCurrentFormState(ndaTermPreferences);
            }
        }

        if (isTermsLocked()) {
            setIsBuyerReadOnly(true);
            setIsSellerReadOnly(true);
        }
    }, [props.activeEventId, props.engagementData, props.events]);

    const isFormValid = () => {
        const f = currentFormState;
        return (
            f.nonDisclosurePeriod !== null &&
            (f.nonSolicitationDvmNamesVisible !== null ||
                f.nonSolicitationDvmNamesBlinded !== null) &&
            (f.nonSolicitationStaffNamesVisible !== null ||
                f.nonSolicitationStaffNamesBlinded !== null)
        );
    };

    const submit = () => {
        setIsLoading(true);
        let payload = currentFormState;
        if (!isMarketplaceBuyer) {
            payload = {
                ...currentFormState,
                party: TimelineParty.Seller,
                buyerId: 0,
            };
        } else {
            payload = {
                ...currentFormState,
                party: TimelineParty.Buyer,
                buyerId: buyer?.id,
            };
        }
        axios
            .post(
                `/api/Marketplace/${props.engagementId}/MarketplaceEventActions/NdaTerm`,
                payload
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(
                        'Something went wrong during saving form',
                        'error'
                    );
                    return;
                }
                // [2.17.3 K7] The terms are saved and the pop-up closes: say so.
                createNotification(
                    isMarketplaceBuyer
                        ? 'Your Data NDA terms were sent to the Seller.'
                        : 'Data NDA terms saved.',
                    'success'
                );
            })
            .catch(() => {
                createNotification('Something went wrong during saving form', 'error');
            })
            .finally(() => {
                props.fetchData();
                setIsLoading(false);
                props.setIsVisible(false);
            });
    };

    const getTermStatus = (
        buyerState: NdaTerms,
        sellerState: NdaTerms,
        isLockedIn: boolean
    ) => {
        const engagementBuyer = props.engagementProcess.buyers.find(
            (f) => f.buyerId === buyerState.buyerId
        );

        const activeEventActionId = props.events.find(
            (e) => e.id === props.activeEventId
        )?.linkedAction?.id;
        const buyerTermsActionState = engagementBuyer?.actionState?.find(
            (f) => f.actionId === activeEventActionId
        );

        if (buyerTermsActionState?.status === TimelineActionStatus.Completed) {
            return NdaTermStatus.NdaDocumentExecuted;
        }

        if (buyerState.agreedTermsToDocumentList?.some((s) => s.isLocked)) {
            return NdaTermStatus.NdaDocumentSent;
        }

        if (buyerState.isPreferences && !isLockedIn) {
            return NdaTermStatus.AwaitingResponse;
        }
        if (buyerState.isPreferences && isLockedIn) {
            return NdaTermStatus.NotInAgreement;
        }
        if (isInAgreement(buyerState)) {
            return NdaTermStatus.InAgreement;
        }
        if (sellerState.dateCreated < buyerState.dateModified) {
            return NdaTermStatus.NotInAgreement;
        }
        if (isLockedIn) {
            return NdaTermStatus.NotInAgreement;
        }
        return NdaTermStatus.AwaitingResponse;
    };

    const isInAgreement = (buyer: NdaTerms) => {
        return buyer.agreedTermsToDocumentList?.length;
    };

    const getNdaTermSelection = () => {
        const event = props.events.find((x) => x.id === props.activeEventId);
        if (!event) return null;

        const action = event.linkedAction;
        if (!action) return null;

        const selection = props.engagementData.ndaTermSelectionList.find(
            (x) => x.id === action.dataNdaTermId
        );

        return selection;
    };

    const getLastTermSelection = (terms: NdaTerms[]) => {
        const result = terms.sort(
            (a, b) =>
                new Date(b.dateCreated).getTime() -
                new Date(a.dateCreated).getTime()
        )[0];
        return result;
    };

    const getMatchScore = (
        buyerTerm: NdaTerms,
        sellerTerm: NdaTerms | null
    ) => {
        if (!sellerTerm) return 0;
        let score = 0;
        if (buyerTerm.nonDisclosurePeriod === sellerTerm.nonDisclosurePeriod) {
            score++;
        }
        const dvmMatch =
            (buyerTerm.nonSolicitationDvmNamesVisible &&
                buyerTerm.nonSolicitationDvmNamesVisible ===
                    sellerTerm.nonSolicitationDvmNamesVisible) ||
            (buyerTerm.nonSolicitationDvmNamesBlinded &&
                buyerTerm.nonSolicitationDvmNamesBlinded ===
                    sellerTerm.nonSolicitationDvmNamesBlinded);
        if (dvmMatch) score++;
        const staffMatch =
            (buyerTerm.nonSolicitationStaffNamesVisible &&
                buyerTerm.nonSolicitationStaffNamesVisible ===
                    sellerTerm.nonSolicitationStaffNamesVisible) ||
            (buyerTerm.nonSolicitationStaffNamesBlinded &&
                buyerTerm.nonSolicitationStaffNamesBlinded ===
                    sellerTerm.nonSolicitationStaffNamesBlinded);
        if (staffMatch) score++;
        return score;
    };

    const getNdaTermPreferences = (buyer: EngagementBuyer): NdaTerms => {
        return {
            id: generateGuid(),
            nonDisclosurePeriod:
                buyer.ndaTermPreferences?.nonDisclosurePeriod || 1,
            nonSolicitationDvmNamesVisible:
                buyer.ndaTermPreferences?.nonSolicitationDvmNamesVisible || 0,
            nonSolicitationDvmNamesBlinded:
                buyer.ndaTermPreferences?.nonSolicitationDvmNamesBlinded || 0,
            nonSolicitationStaffNamesVisible:
                buyer.ndaTermPreferences?.nonSolicitationStaffNamesVisible || 0,
            nonSolicitationStaffNamesBlinded:
                buyer.ndaTermPreferences?.nonSolicitationStaffNamesBlinded || 0,
            buyerId: buyer.buyerId,
            party: TimelineParty.Buyer,
            dateCreated: new Date(),
            dateModified: new Date(),
            isPreferences: true,
            agreedTermsToDocumentList: [],
        };
    };

    const getNdaTermPreferencesForMarketplaceBuyer = (
        buyer: MarketplaceBuyer
    ) => {
        return {
            id: generateGuid(),
            nonDisclosurePeriod:
                buyer.profile?.currentNdaTermPreferences?.nonDisclosurePeriod ||
                DataNdaTermsType.OneYear,
            nonSolicitationDvmNamesVisible:
                buyer.profile?.currentNdaTermPreferences
                    ?.nonSolicitationDvmNamesVisible || 0,
            nonSolicitationDvmNamesBlinded:
                buyer.profile?.currentNdaTermPreferences
                    ?.nonSolicitationDvmNamesBlinded || 0,
            nonSolicitationStaffNamesVisible:
                buyer.profile?.currentNdaTermPreferences
                    ?.nonSolicitationStaffNamesVisible || 0,
            nonSolicitationStaffNamesBlinded:
                buyer.profile?.currentNdaTermPreferences
                    ?.nonSolicitationStaffNamesBlinded || 0,
            buyerId: buyer.id,
            party: TimelineParty.Buyer,
            dateCreated: new Date(),
            dateModified: new Date(),
            isPreferences: true,
        } as NdaTerms;
    };

    const isBuyerActive = (buyerId: number) => {
        if (!buyerId) return false;
        const buyers = props.engagementProcess?.buyers;
        if (!buyers) return false;
        return buyers.some((x) => x.buyerId === buyerId && x.isActive);
    };

    const isAnyBuyerActive = () => {
        const buyers = props.engagementProcess?.buyers;
        if (!buyers) return false;
        return buyers.some((x) => x.isActive);
    };

    const isRejectedStatus = (status: MarketplaceOpportunityStatus) =>
        status === MarketplaceOpportunityStatus.RejectedByBuyer ||
        status === MarketplaceOpportunityStatus.RejectedBySeller;

    const getLatestActionState = (buyer: EngagementBuyer) =>
        [...(buyer.actionState ?? [])].sort(
            (a, b) =>
                new Date(b.dateCreated).getTime() -
                new Date(a.dateCreated).getTime()
        )[0];

    const isTermOfRejectedBuyer = (term: NdaTerms) =>
        getBuyerRejectionStatus(
            props.engagementProcess?.buyers?.find(
                (y) => y.buyerId === term.buyerId
            )
        ) !== null;

    const getBuyerRejectionStatus = (buyer: EngagementBuyer) => {
        if (!buyer || buyer.isActive) {
            return null;
        }
        const activeActionState = buyer.actionState?.find(
            (s) => s.actionId === activeEventActionId
        );
        if (activeActionState) {
            if (isRejectedStatus(activeActionState.buyerStatus)) {
                return activeActionState.buyerStatus;
            }
            const rejectedState = buyer.actionState.find((s) =>
                isRejectedStatus(s.buyerStatus)
            );
            return rejectedState?.buyerStatus ?? null;
        }
        const latestActionState = getLatestActionState(buyer);
        const isRejectedAfterCompletedAction =
            latestActionState &&
            isRejectedStatus(latestActionState.buyerStatus) &&
            latestActionState.status === TimelineActionStatus.Completed;
        return isRejectedAfterCompletedAction
            ? latestActionState.buyerStatus
            : null;
    };

    const getRejectedEngagementBuyers = () =>
        (props.engagementProcess?.buyers ?? []).filter(
            (b) => getBuyerRejectionStatus(b) !== null
        );

    const addDefaultNdaTerms = (
        ndaTerms: NdaTerms[],
        includingUnselected = true,
        includingRejected = false
    ) => {
        const buyers = props.engagementProcess?.buyers;
        if (!buyers) return ndaTerms;
        const buyersToAdd = buyers.filter(
            (x) =>
                (x.isActive ||
                    (includingRejected &&
                        getBuyerRejectionStatus(x) !== null)) &&
                (includingUnselected || isBuyerIncluded(x.buyerId)) &&
                !ndaTerms.some((y) => y.buyerId === x.buyerId)
        );
        const termsToAdd = buyersToAdd.map((x) => getNdaTermPreferences(x));
        const result = ndaTerms.concat(termsToAdd);
        return result;
    };

    const isBuyerIncluded = (buyerId?: number) =>
        buyersInclusionStatuses.find(
            (b) => b.buyerId === (buyerId ?? selectedBuyerId)
        )?.active ?? false;

    const handleBuyersCheckboxChange = (buyerId: number) => {
        setSelectedBuyerId(buyerId);
        setIsConfirmationPopupVisible(true);
    };

    const handleBuyerStatusChange = () => {
        setIsSwitchingBuyer(true);
        const currentState = isBuyerIncluded();
        const action = !currentState ? 'Select' : 'Unselect';
        const errorText = `An Error occured during buyer ${
            !currentState ? 'un' : ''
        }selection.`;
        axios
            .post(
                `/api/Marketplace/${props.engagementId}/MarketplaceEventActions/${action}`,
                { buyerIds: [selectedBuyerId], eventId: props.activeEventId }
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(errorText, 'error');
                } else {
                    setBuyersInclusionStatuses([
                        ...buyersInclusionStatuses.map((b) =>
                            b.buyerId === selectedBuyerId
                                ? { buyerId: b.buyerId, active: !b.active }
                                : b
                        ),
                    ]);
                    props.fetchData();
                }
            })
            .catch((error) => {
                createNotification(errorText, 'error');
            })
            .finally(() => {
                setIsSwitchingBuyer(false);
            });
    };

    const renderBuyersNdaTermsList = () => {
        const selection = getNdaTermSelection();
        let buyerNdaTerms = getBuyersSelections(true, true) ?? [];
        const sellerNdaTerms =
            selection?.ndaTermsList.filter(
                (x) => x.party === TimelineParty.Seller
            ) ?? [];
        const lastSellerNda = getLastTermSelection(sellerNdaTerms);

        buyerNdaTerms = addDefaultNdaTerms(buyerNdaTerms, true, true);

        const buyers = props.engagementProcess.buyers;

        const sortedBuyerNdaTerms = [...buyerNdaTerms].sort((a, b) => {
            const aRejected = isTermOfRejectedBuyer(a);
            const bRejected = isTermOfRejectedBuyer(b);
            if (aRejected !== bRejected) {
                return aRejected ? 1 : -1;
            }
            const aSeller =
                sellerNdaTerms.find(
                    (y) =>
                        y.id ===
                        a.agreedTermsToDocumentList?.find((f) => f)
                            ?.agreedSellerNdaTermsId
                ) || lastSellerNda;
            const bSeller =
                sellerNdaTerms.find(
                    (y) =>
                        y.id ===
                        b.agreedTermsToDocumentList?.find((f) => f)
                            ?.agreedSellerNdaTermsId
                ) || lastSellerNda;
            const aScore = getMatchScore(a, aSeller);
            const bScore = getMatchScore(b, bSeller);
            if (bScore !== aScore) {
                return bScore - aScore;
            }

            const aHasAgreement = !!a.agreedTermsToDocumentList?.length;
            const bHasAgreement = !!b.agreedTermsToDocumentList?.length;
            if (aHasAgreement && !bHasAgreement) return -1;
            if (!aHasAgreement && bHasAgreement) return 1;

            return 0;
        });

        return sortedBuyerNdaTerms.length > 0 ? (
            <div className="nda-terms-list">
                {sortedBuyerNdaTerms.map((x, index) => {
                    const currentBuyer = buyers.find(
                        (y) => y.buyerId === x.buyerId
                    );

                    const sellerState =
                        (!!!x.agreedTermsToDocumentList
                            ? lastSellerNda
                            : sellerNdaTerms.find(
                                  (y) =>
                                      y.id ===
                                      x.agreedTermsToDocumentList?.find(
                                          (f) => f
                                      )?.agreedSellerNdaTermsId
                              )) ?? currentFormState;
                    const termStatus = NdaTermStatusList.find(
                        (y) =>
                            y.id ===
                            getTermStatus(x, sellerState, selection?.isLockedIn)
                    );
                    const rejectionStatus = MarketplaceOpportunityStatusList.find(
                        (y) => y.id === getBuyerRejectionStatus(currentBuyer)
                    );
                    const displayedStatus = rejectionStatus ?? termStatus;

                    return (
                        <div className="nda-terms-list-item">
                            <div className="nda-terms-list-item-header">
                                <div
                                    className="link-text"
                                    onClick={() => {
                                        setSelectedBuyerId(
                                            currentBuyer.buyerId
                                        );
                                        setIsBuyerProfilePopupVisible(true);
                                    }}>
                                    {currentBuyer?.companyName}
                                </div>
                                {(rejectionStatus || lastSellerNda) && (
                                    <>
                                        {'-'}
                                        <div
                                            className="status"
                                            style={{
                                                color: displayedStatus.color,
                                            }}>
                                            {displayedStatus.name}
                                        </div>
                                    </>
                                )}
                                <div className="buyer-checkbox-wrapper">
                                    <div className="input-wrapper flex-no-wrap">
                                        <div className="input-section">
                                            <label className="custom-checkbox-wrapper">
                                                <input
                                                    type="checkbox"
                                                    checked={isBuyerIncluded(
                                                        currentBuyer.buyerId
                                                    )}
                                                    disabled={
                                                        isAdminView ||
                                                        isBroker ||
                                                        isObserver ||
                                                        isBuyerTermsLocked(
                                                            x.buyerId
                                                        ) ||
                                                        isEventOrEngagementCompleted ||
                                                        !!rejectionStatus
                                                    }
                                                    onChange={() =>
                                                        handleBuyersCheckboxChange(
                                                            currentBuyer.buyerId
                                                        )
                                                    }
                                                />
                                                <span
                                                    className={`custom-icon ${
                                                        isBuyerIncluded(
                                                            currentBuyer.buyerId
                                                        )
                                                            ? 'checked green'
                                                            : 'unchecked'
                                                    }`}>
                                                    {isBuyerIncluded(
                                                        currentBuyer.buyerId
                                                    )
                                                        ? <FontAwesomeIcon className='checkmark green' icon={faCheck} />
                                                        : <FontAwesomeIcon icon={faXmark} />}
                                                </span>
                                            </label>
                                        </div>
                                        <div className="">
                                            Include this Buyer
                                            <InfoTooltip
                                                text={
                                                    'Include this Buyer in the Data NDA Terms Negotiation & Execution'
                                                }
                                                idText={`buyer-checkmark-tooltip-${index}`}></InfoTooltip>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <NdaTermsForm
                                setCurrentFormState={setCurrentFormState}
                                currentFormState={currentFormState}
                                isSummaryView={false}
                                isBuyerView={false}
                                allTermsSelections={[sellerState, x]}
                                isSellerReadOnly={true}
                                isBuyerReadOnly={true}
                                tooltipKey={`buyer-${index}-terms`}></NdaTermsForm>
                        </div>
                    );
                })}
            </div>
        ) : (
            <></>
        );
    };

    const isSummaryView = () => {
        let selection = getNdaTermSelection();
        return (
            !isMarketplaceBuyer &&
            selection &&
            selection.ndaTermsList?.length > 0
        );
    };

    const isTermsLocked = () => {
        const selection = getNdaTermSelection();
        return selection?.isLockedIn;
    };

    const isBuyerTermsLocked = (buyerId: number) => {
        const selection = getNdaTermSelection();
        const buyerTerms = selection?.ndaTermsList?.find(
            (f) => f.buyerId === buyerId
        );
        const isBuyerTermsLocked = buyerTerms?.agreedTermsToDocumentList?.some(
            (s) => s.isLocked
        );
        return isBuyerTermsLocked;
    };

    const isSendNdaButtonDisabled = () => {
        const buyerNdaTerms = getBuyersSelections() ?? [];
        const selectedBuyers = buyerNdaTerms.filter((f) =>
            buyersInclusionStatuses.some(
                (s) => s.buyerId === f.buyerId && s.active
            )
        );
        const selectedBuyersInAgreement = selectedBuyers.filter(
            (f) => f.agreedTermsToDocumentList?.length
        );

        const isAnyBuyerInAgreementNotLocked = selectedBuyersInAgreement.some(
            (s) => {
                const reversed = [...s.agreedTermsToDocumentList].reverse();
                const isNotLocked = !reversed.find((f) => f).isLocked;
                return isNotLocked;
            }
        );

        return !isAnyBuyerInAgreementNotLocked;
    };

    const isSendRemainingPartiesButtonActive = () => {
        const buyerNdaTerms = getBuyersSelections() ?? [];

        const selectedBuyers = buyerNdaTerms.filter((f) =>
            buyersInclusionStatuses.some(
                (s) => s.buyerId === f.buyerId && s.active
            )
        );
        const selectedBuyersInAgreement = selectedBuyers.filter(
            (f) => f.agreedTermsToDocumentList?.length
        );

        const isAnyBuyerInAgreementNotLocked = selectedBuyersInAgreement.some(
            (s) => {
                const reversed = [...s.agreedTermsToDocumentList].reverse();
                const isNotLocked = !reversed.find((f) => f).isLocked;
                return isNotLocked;
            }
        );

        const buyersNotInAgreement = buyerNdaTerms.filter(
            (f) => !f.agreedTermsToDocumentList?.length
        );

        return !isAnyBuyerInAgreementNotLocked && buyersNotInAgreement?.length;
    };

    const areTermsChanged = () => {
        if (!lastFormState) {
            return true;
        }

        return (
            currentFormState.nonDisclosurePeriod !==
                lastFormState.nonDisclosurePeriod ||
            currentFormState.nonSolicitationDvmNamesVisible !==
                lastFormState.nonSolicitationDvmNamesVisible ||
            currentFormState.nonSolicitationDvmNamesBlinded !==
                lastFormState.nonSolicitationDvmNamesBlinded ||
            currentFormState.nonSolicitationStaffNamesVisible !==
                lastFormState.nonSolicitationStaffNamesVisible ||
            currentFormState.nonSolicitationStaffNamesBlinded !==
                lastFormState.nonSolicitationStaffNamesBlinded
        );
    };

    const getEventDeadline = () => {
        const activeEvent = props.events.find(
            (x) => x.id === props.activeEventId
        );
        return getEventDeadlineDateUtc(
            props.engagementProcess.startDate,
            activeEvent
        );
    };

    const renderNdaTermsForm = () => {
        let selection = getNdaTermSelection();
        let terms = [] as NdaTerms[];
        if (selection) {
            if (isMarketplaceBuyer) {
                const sellerNdaTerms = selection.ndaTermsList.filter(
                    (x) => x.party === TimelineParty.Seller
                );

                if (sellerNdaTerms.length > 0) {
                    const lastSellerNda = getLastTermSelection(sellerNdaTerms);
                    const sellerState = !!!currentFormState
                        .agreedTermsToDocumentList?.length
                        ? lastSellerNda
                        : sellerNdaTerms.find(
                              (y) =>
                                  y.id ===
                                  currentFormState.agreedTermsToDocumentList?.find(
                                      (f) => f
                                  )?.agreedSellerNdaTermsId
                          );
                    terms.push(sellerState);
                }
            } else {
                const filteredTerms = selection.ndaTermsList.filter(
                    (x) =>
                        x.party === TimelineParty.Seller ||
                        (isBuyerActive(x.buyerId) && isBuyerIncluded(x.buyerId))
                );
                terms = terms.concat(filteredTerms);
            }
        }
        if (!isMarketplaceBuyer) {
            terms = addDefaultNdaTerms(terms, false);
        }
        terms.push(currentFormState);

        let sendTermsButton = <></>;

        if (!isMarketplaceBuyer) {
            sendTermsButton = isSendRemainingPartiesButtonActive() ? (
                <button
                    className="btn btn-primary"
                    onClick={() => {
                        setIsFinalLockIn(true);
                        setIsLockInPopupVisible(true);
                    }}>
                    {'Send NDA to all Remaining Parties'}
                </button>
            ) : (
                <button
                    className="btn btn-primary"
                    disabled={isSendNdaButtonDisabled()}
                    onClick={() => {
                        setIsFinalLockIn(false);
                        setIsLockInPopupVisible(true);
                    }}>
                    {'Send NDA to Parties in Agreement'}
                </button>
            );
        }

        return (
            <div>
                <NdaTermsForm
                    setCurrentFormState={setCurrentFormState}
                    currentFormState={currentFormState}
                    isSellerReadOnly={isSellerReadOnly}
                    isSummaryView={!isMarketplaceBuyer}
                    isBuyerView={!!isMarketplaceBuyer}
                    allTermsSelections={terms}
                    lastSellerState={lastFormState}
                    isBuyerReadOnly={isBuyerReadOnly}
                    tooltipKey={'seller-terms'}></NdaTermsForm>

                <div className="buttons-wrapper">
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => props.setIsVisible(false)}>
                        Close
                    </button>
                    {!isTermsLocked() &&
                        !isEventOrEngagementCompleted &&
                        !isAdminView &&
                        !isBroker &&
                        !isObserver && (
                            <>
                                {isSummaryView() && areTermsChanged() ? (
                                    <ButtonLoader
                                        disabled={
                                            (!isMarketplaceBuyer &&
                                                (!isFormValid() ||
                                                    isSellerReadOnly)) ||
                                            (isMarketplaceBuyer &&
                                                (!isFormValid() ||
                                                    isBuyerReadOnly))
                                        }
                                        onClick={() => {
                                            submit();
                                        }}
                                        className="btn btn-primary"
                                        buttonText={'Update New Proposed Terms'}
                                        isLoading={isLoading}
                                        loaderButtonText={
                                            'Update New Proposed Terms'
                                        }
                                    />
                                ) : (
                                    <>
                                        {!isSummaryView() && (
                                            <ButtonLoader
                                                disabled={
                                                    (!isMarketplaceBuyer &&
                                                        (!isFormValid() ||
                                                            isSellerReadOnly)) ||
                                                    (isMarketplaceBuyer &&
                                                        (!isFormValid() ||
                                                            isBuyerReadOnly))
                                                }
                                                onClick={() => {
                                                    submit();
                                                }}
                                                className="btn btn-primary"
                                                buttonText={'Submit'}
                                                isLoading={isLoading}
                                                loaderButtonText={'Submit'}
                                            />
                                        )}
                                        {isSummaryView() &&
                                            !isEventOrEngagementCompleted &&
                                            sendTermsButton}
                                    </>
                                )}

                                {isSummaryView() &&
                                    !isEventOrEngagementCompleted && (
                                        <>
                                            <button
                                                id={'extend-deadline-button'}
                                                className="btn btn-primary"
                                                onClick={() =>
                                                    setIsExtendDeadlinePopupVisible(
                                                        true
                                                    )
                                                }>
                                                Extend Deadline
                                            </button>
                                            <PortalTooltip
                                                anchorId={
                                                    'extend-deadline-button'
                                                }
                                                delayShow={500}
                                                place="top"
                                                content={
                                                    'You will pay penalty for every day of extension.'
                                                }
                                            />
                                        </>
                                    )}
                            </>
                        )}
                </div>
                {(isAnyBuyerActive() ||
                    getRejectedEngagementBuyers().length > 0) && (
                    <>
                        {!isMarketplaceBuyer && <div className="separator" />}
                        {!isMarketplaceBuyer && renderBuyersNdaTermsList()}
                    </>
                )}
            </div>
        );
    };

    const getLastSellerSelection = () => {
        const selection = getNdaTermSelection();
        if (!selection) return null;
        const sellerNdaTerms = selection?.ndaTermsList.filter(
            (x) => x.party === TimelineParty.Seller
        );
        if (sellerNdaTerms.length > 0) {
            return getLastTermSelection(sellerNdaTerms);
        }
        return null;
    };

    const getBuyersSelections = (
        includingUnselected = false,
        includingRejected = false
    ) => {
        const selection = getNdaTermSelection();
        let buyerSelections = selection?.ndaTermsList.filter(
            (x) => x.party === TimelineParty.Buyer
        );

        const buyers = props.engagementProcess?.buyers;
        const visibleBuyers = buyers.filter(
            (x) =>
                (x.isActive &&
                    (includingUnselected || isBuyerIncluded(x.buyerId))) ||
                (includingRejected && getBuyerRejectionStatus(x) !== null)
        );
        const filteredBuyerSelections = buyerSelections?.filter((x) =>
            visibleBuyers.some((y) => y.buyerId === x.buyerId)
        );
        return filteredBuyerSelections;
    };

    const renderSummaryRow = () => {
        const selection = getNdaTermSelection();
        let buyerSelections = getBuyersSelections(true, true) ?? [];
        buyerSelections = addDefaultNdaTerms(buyerSelections, true, true);

        const negotiatingSelections = buyerSelections.filter(
            (x) => !isTermOfRejectedBuyer(x)
        );
        const agreedBuyers = negotiatingSelections.filter(
            (x) => !!x.agreedTermsToDocumentList?.length
        );
        const sellerNdaTerms = selection.ndaTermsList.filter(
            (x) => x.party === TimelineParty.Seller
        );

        const lastSellerNda = getLastTermSelection(sellerNdaTerms);
        const notAgreedBuyers = negotiatingSelections.filter(
            (x) =>
                !!!x.agreedTermsToDocumentList?.length &&
                (x.isPreferences
                    ? selection?.isLockedIn
                    : lastSellerNda.dateCreated < x.dateModified ||
                      selection?.isLockedIn)
        );

        const awaitingResponseBuyersCount =
            negotiatingSelections.length -
            agreedBuyers.length -
            notAgreedBuyers.length;

        const totalBuyersCount = buyerSelections.length;

        return (
            <Row>
                <div className="summary-row">
                    <div className="summary-row-item">
                        <b>Total Buyers: </b>
                        <span>
                            {' '}
                            {totalBuyersCount > 0 ? totalBuyersCount : 'NA'}
                        </span>
                    </div>
                    <div className="summary-row-item">
                        <b>Terms Agreed:</b>{' '}
                        <span>
                            {' '}
                            {agreedBuyers.length > 0
                                ? agreedBuyers.length
                                : 'NA'}
                        </span>
                    </div>
                    <div className="summary-row-item">
                        <b>{`Terms Not ${
                            selection?.isLockedIn ? '' : 'Yet '
                        }Agreed:`}</b>{' '}
                        <span>
                            {' '}
                            {notAgreedBuyers.length > 0
                                ? notAgreedBuyers.length
                                : 'NA'}
                        </span>
                    </div>
                    <div className="summary-row-item">
                        <b> Awaiting Response:</b>{' '}
                        <span>
                            {' '}
                            {awaitingResponseBuyersCount > 0
                                ? awaitingResponseBuyersCount
                                : 'NA'}
                        </span>
                    </div>
                </div>
            </Row>
        );
    };

    const getBuyersWithReceivedDocuments = (
        process: EngagementProcess = props.engagementProcess
    ) => {
        const selection = getNdaTermSelection();
        let alreadySelectedBuyers: number[] = [];
        if (selection && selection.ndaTermsList) {
            alreadySelectedBuyers = selection.ndaTermsList
                .filter((ndaTerm) => {
                    const engagementBuyer = process.buyers.find(
                        (f) => f.buyerId === ndaTerm.buyerId
                    );

                    if (!engagementBuyer?.isActive) {
                        return false;
                    }

                    const activeEventActionId = props.events.find(
                        (e) => e.id === props.activeEventId
                    )?.linkedAction?.id;
                    const buyerTermsActionState =
                        engagementBuyer?.actionState?.find(
                            (f) => f.actionId === activeEventActionId
                        );
                    if (
                        buyerTermsActionState?.status ===
                        TimelineActionStatus.Completed
                    ) {
                        return true;
                    }
                    if (
                        ndaTerm.agreedTermsToDocumentList?.some(
                            (s) => s.isLocked
                        )
                    ) {
                        return true;
                    }
                    return false;
                })
                .map((ndaTerm) => ndaTerm.buyerId);
        }
        return alreadySelectedBuyers;
    };

    const getBuyersInAgreementWithoutReceivedDocument = () => {
        const selection = getNdaTermSelection();
        if (!selection) return [];
        const buyerNdaTerms = getBuyersSelections() ?? [];
        const buyersToSend = buyerNdaTerms
            .filter(
                (ndaTerm) =>
                    ndaTerm.agreedTermsToDocumentList?.length &&
                    !ndaTerm.agreedTermsToDocumentList.some((s) => s.isLocked)
            )
            .map((ndaTerm) => ndaTerm.buyerId);
        return buyersToSend;
    };

    const getBuyersNotInAgreementWithoutReceivedDocument = () => {
        const selection = getNdaTermSelection();
        if (!selection) return [];
        const buyerNdaTerms = getBuyersSelections() ?? [];
        const buyersToSend = buyerNdaTerms
            .filter(
                (ndaTerm) =>
                    !ndaTerm.agreedTermsToDocumentList?.length &&
                    !getBuyersWithReceivedDocuments().includes(ndaTerm.buyerId)
            )
            .map((ndaTerm) => ndaTerm.buyerId);
        return buyersToSend;
    };

    const getPaymentDetails = () => {
        const buyersToSelect = isFinalLockIn
            ? getBuyersNotInAgreementWithoutReceivedDocument()
            : getBuyersInAgreementWithoutReceivedDocument();
        const levelOfService = props.levelOfService;

        const brokreagePricing =
            PricesCalculationHelper.getBrokeragePricingByLevelOfService(
                levelOfService,
                marketplacePricing
            );

        const scheduleRow = PricesCalculationHelper.getScheduleRow(brokreagePricing.transactionFeeSchedule, props.engagementProcess.startLtmRevenue);
        const pricePerBuyer = scheduleRow?.dataNdaFeePerBuyer ?? 0;
        const firstXAsOne = brokreagePricing.transactionFees.dataNdaFirstXCount;
        const creditablePortion =
            brokreagePricing.transactionFees.dataNdaFeeCreditable;

        // [#1309] Buyers paid for on this step before are sent the Data NDA again at no charge.
        const { chargedBuyersCount, totalPrice: orderTotalPrice } =
            PricesCalculationHelper.priceBuyerSelection(
                buyersToSelect,
                alreadySelectedBuyerIds,
                paidBeforeBuyerIds,
                firstXAsOne,
                pricePerBuyer
            );
        var creditableValue = (creditablePortion * orderTotalPrice) / 100;
        const result: SelectionPaymentDetails = {
            selectedBuyerIds: buyersToSelect,
            pricePerBuyer: pricePerBuyer,
            totalPrice: orderTotalPrice,
            firstNAsOneCount: firstXAsOne,
            eventId: props.activeEventId,
            creditablePortion: creditableValue,
            chargedBuyersCount,
        };
        return result;
    };

    const handlePayAndSendClick = () => {
        const paymentDetails = getPaymentDetails();
        setSelectionPaymentDetails(paymentDetails);

        if (!isSfas(props.levelOfService)) {
            setIsPaymentPopupVisible(true);
            return;
        }

        const selection = getNdaTermSelection();
        if (!selection) return;
        axios
            .post(
                `/api/Marketplace/${props.engagementId}/MarketplaceEventActions/` +
                    `LockInNdaTerms/${selection.id}${
                        isFinalLockIn ? '/final' : ''
                    }`
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An Error occured during buyer selection.',
                        'error'
                    );
                } else handleScussessfulPayment();
            })
            .catch(() => {
                createNotification(
                    'An Error occured during buyer selection.',
                    'error'
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleScussessfulPayment = () => {
        // [2.17.3 K7] The NDA went out (after the selection's payment, or straight away on SFAS): say so.
        createNotification(
            isFinalLockIn
                ? 'The Data NDA was sent to all remaining parties.'
                : 'The Data NDA was sent to the parties in agreement.',
            'success'
        );
        props.fetchData();
        props.setIsVisible(false);
        setIsPaymentPopupVisible(false);
    };

    return (
        <>
            <Rodal
                visible={props.isVisible}
                onClose={() => props.setIsVisible(false)}
                animation="slideUp"
                className="data-nda-terms-popup"
                showMask={true}
                width={700}
                showCloseButton={true}>
                <ModalHeader>
                    <b>Data NDA Terms</b>
                </ModalHeader>
                <ModalBody
                    style={{ marginBottom: isMarketplaceBuyer ? '0' : '2rem' }}>
                    <div className="data-nda-terms-popup-form">
                        <Row>
                            {isMarketplaceBuyer ? (
                                <>
                                    <p>
                                        The seller has specified their preferred
                                        terms for the Data NDA. To access the
                                        practice investment presentation and
                                        detailed financial model, you must
                                        review these terms and provide feedback.
                                        Your feedback will be sent to the seller
                                        for review and possible term updates. If
                                        you cannot reach agreement with the
                                        seller on the terms, you will not be
                                        able to execute the NDA or access the
                                        materials. Please review and provide
                                        your response below.
                                    </p>
                                    <div className="color-legend">
                                        <div>
                                            <FontAwesomeIcon icon={faCheck} className="faded-black" />{' '}
                                            Your selection — matches seller's
                                            terms, but they're offering better
                                        </div>
                                        <div>
                                            <FontAwesomeIcon icon={faCheck} className="black" />{' '}
                                            Your selection
                                        </div>
                                        <div>
                                            <FontAwesomeIcon icon={faCheck} className="green" />{' '}
                                            Your selection — matches seller's
                                            terms
                                        </div>
                                        <div>
                                            <FontAwesomeIcon icon={faCheck} className="red" />{' '}
                                            Seller's selection — less favorable
                                            than yours
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p>
                                        Please specify your preferred terms for
                                        the mutual Data NDA which will be
                                        executed with your selected buyers prior
                                        to granting them access to your
                                        practice's sensitive and competitive
                                        information in the practice information
                                        package and the financial model.
                                    </p>{' '}
                                    <div className="color-legend">
                                        <div>
                                            <FontAwesomeIcon icon={faCheck} className="black" />{' '}
                                            Your selections
                                        </div>
                                        <div>
                                            <FontAwesomeIcon icon={faCheck} className="green" />{' '}
                                            Buyer selection in agreement with
                                            your selections
                                        </div>
                                        <div>
                                            <FontAwesomeIcon icon={faCheck} className="red" />{' '}
                                            Buyer selection not in agreement
                                            with your selections
                                        </div>
                                    </div>
                                </>
                            )}
                        </Row>
                        {!isMarketplaceBuyer ? (
                            <Row className="summary-title">
                                <b>Summary View</b>
                            </Row>
                        ) : (
                            <></>
                        )}
                        {isSummaryView() && renderSummaryRow()}

                        {renderNdaTermsForm()}
                    </div>
                </ModalBody>
            </Rodal>

            {isBuyerProfilePopupVisible ? (
                <BuyerProfilePreviewPopup
                    buyerId={selectedBuyerId}
                    close={() => setIsBuyerProfilePopupVisible(false)}
                />
            ) : (
                <></>
            )}

            {isConfirmationPopupVisible ? (
                <ConfirmationModal
                    header={`${isBuyerIncluded() ? 'Exclude' : 'Include'} ${
                        props.engagementProcess.buyers?.find(
                            (b) => b.buyerId === selectedBuyerId
                        )?.companyName ?? 'Buyer'
                    }`}
                    isVisible={isConfirmationPopupVisible}
                    setIsVisible={(visible: boolean) => {
                        setIsConfirmationPopupVisible(visible);
                        setSelectedBuyerId(0);
                    }}
                    confirmationText={`Are you sure you want to ${
                        isBuyerIncluded() ? 'exclude' : 'include'
                    } this Buyer in the Data NDA Terms Negotiation & Execution?`}
                    params={''}
                    onConfirm={() => handleBuyerStatusChange()}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                    isLoading={isSwitchingBuyer}
                />
            ) : (
                <></>
            )}

            {isLockInPopupVisible && getLastSellerSelection() != null ? (
                <LockInPopup
                    lastSellerSelection={getLastSellerSelection()}
                    isFinalLockIn={isFinalLockIn}
                    isVisible={isLockInPopupVisible}
                    setIsVisible={setIsLockInPopupVisible}
                    ndaTermSelectionId={getNdaTermSelection()?.id || ''}
                    engagementId={props.engagementId}
                    onSubmit={handlePayAndSendClick}
                />
            ) : (
                <></>
            )}

            {isPaymentPopupVisible ? (
                <BuyerSelectionPaymentComponent
                    isVisible={isPaymentPopupVisible}
                    actionType={BuyerSelectionType.DataNda}
                    setIsVisible={setIsPaymentPopupVisible}
                    paymentSuccess={() => handleScussessfulPayment()}
                    engagementId={props.engagementId}
                    selectedBuyers={selectionPaymentDetails.selectedBuyerIds}
                    chargedBuyersCount={selectionPaymentDetails.chargedBuyersCount}
                    pricePerBuyer={selectionPaymentDetails.pricePerBuyer}
                    creditablePortion={
                        selectionPaymentDetails.creditablePortion
                    }
                    totalPrice={selectionPaymentDetails.totalPrice}
                    firstNAsOneCount={selectionPaymentDetails.firstNAsOneCount}
                    eventId={selectionPaymentDetails.eventId}
                    isFinalLock={isFinalLockIn}
                />
            ) : (
                <></>
            )}

            {isExtendDeadlinePopupVisible ? (
                <ExtendDeadlinePopup
                    isVisible={isExtendDeadlinePopupVisible}
                    setIsVisible={setIsExtendDeadlinePopupVisible}
                    eventId={props.activeEventId}
                    eventDeadline={getEventDeadline()}
                    engagementId={props.engagementId}
                    fetchData={props.fetchData}
                    engagementProcess={props.engagementProcess}
                    levelOfService={props.levelOfService}
                    mode={'dataNdaTerms'}
                />
            ) : (
                <></>
            )}
        </>
    );
};

export default NdaTermsPopup;
